import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  TextField,
  Select,
  MenuItem,
  InputAdornment,
  Divider,
  Typography,
} from "@mui/material";
import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
} from "libphonenumber-js";

const PRIORITY_COUNTRIES = ["AU", "NZ", "MY", "SG"];

const getFlagEmoji = (code) =>
  code
    .toUpperCase()
    .split("")
    .map((c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65))
    .join("");

const countryDisplayNames = new Intl.DisplayNames(["en"], { type: "region" });
const getCountryName = (code) => {
  try {
    return countryDisplayNames.of(code) ?? code;
  } catch {
    return code;
  }
};

const buildCountryOptions = () => {
  const allCountries = getCountries();
  const prioritized = PRIORITY_COUNTRIES.filter((c) =>
    allCountries.includes(c)
  );
  const others = allCountries
    .filter((c) => !PRIORITY_COUNTRIES.includes(c))
    .sort((a, b) => getCountryName(a).localeCompare(getCountryName(b)));
  return { prioritized, others };
};

const PhoneField = ({
  value,
  onChange,
  addressCountryCode,
  label = "Phone",
  error,
  helperText,
  ...rest
}) => {
  const [country, setCountry] = useState(
    addressCountryCode?.toUpperCase() || "AU"
  );
  const [localNumber, setLocalNumber] = useState("");
  const isInternalChange = useRef(false);

  const countryOptions = useMemo(buildCountryOptions, []);

  const buildFullNumber = useCallback((countryCode, numberInput) => {
    if (!numberInput) return "";
    try {
      const parsed = parsePhoneNumberFromString(numberInput, countryCode);
      if (parsed) return parsed.number;
    } catch {
      // fall through to manual construction
    }
    const dialCode = getCountryCallingCode(countryCode);
    const cleaned = numberInput.replace(/^0+/, "");
    return `+${dialCode}${cleaned}`;
  }, []);

  // Parse incoming value from parent (skip when change originated from us)
  useEffect(() => {
    if (isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }
    if (!value) {
      setLocalNumber("");
      return;
    }
    const ctx = addressCountryCode?.toUpperCase() || country;
    try {
      const parsed = parsePhoneNumberFromString(value, ctx);
      if (parsed) {
        if (parsed.country) setCountry(parsed.country);
        setLocalNumber(parsed.nationalNumber);
        return;
      }
    } catch {
      // fall through
    }
    setLocalNumber(value);
  }, [value]);

  // Sync country when the address country changes
  useEffect(() => {
    if (!addressCountryCode) return;
    const code = addressCountryCode.toUpperCase();
    setCountry((prev) => {
      if (prev === code) return prev;
      if (localNumber) {
        const fullNumber = buildFullNumber(code, localNumber);
        isInternalChange.current = true;
        onChange(fullNumber);
      }
      return code;
    });
  }, [addressCountryCode]);

  const handleCountryChange = (e) => {
    const newCountry = e.target.value;
    setCountry(newCountry);
    const fullNumber = buildFullNumber(newCountry, localNumber);
    isInternalChange.current = true;
    onChange(fullNumber);
  };

  const handleNumberChange = (e) => {
    const num = e.target.value;
    setLocalNumber(num);
    const fullNumber = buildFullNumber(country, num);
    isInternalChange.current = true;
    onChange(fullNumber);
  };

  const renderOption = (code) => (
    <MenuItem key={code} value={code}>
      <Typography variant="body2" component="span" sx={{ mr: 0.5 }}>
        {getFlagEmoji(code)}
      </Typography>
      +{getCountryCallingCode(code)}{" "}
      <Typography
        variant="caption"
        color="text.secondary"
        component="span"
        sx={{ ml: 0.5 }}
      >
        {getCountryName(code)}
      </Typography>
    </MenuItem>
  );

  return (
    <TextField
      fullWidth
      label={label}
      margin="dense"
      variant="outlined"
      value={localNumber}
      onChange={handleNumberChange}
      error={error}
      helperText={helperText}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Select
              value={country}
              onChange={handleCountryChange}
              variant="standard"
              disableUnderline
              renderValue={(code) =>
                `${getFlagEmoji(code)} +${getCountryCallingCode(code)}`
              }
              sx={{
                minWidth: 100,
                "& .MuiSelect-select": { py: 0, display: "flex", alignItems: "center" },
              }}
              MenuProps={{
                PaperProps: { sx: { maxHeight: 350 } },
              }}
            >
              {countryOptions.prioritized.map(renderOption)}
              <Divider />
              {countryOptions.others.map(renderOption)}
            </Select>
          </InputAdornment>
        ),
      }}
      {...rest}
    />
  );
};

export default PhoneField;
